import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../Components/ConfirmationModal";
import { updatePartnershipContractStatus } from "../Services/partnerService";
import { getAdminProfile, getPrivateFileBlob } from "../Services/userService";
import contractTemplates from "../Components/contractTemplates";

export default function FreightAgreement() {
    const contractRef = useRef(null);
    const { type } = useParams();
    const partnerId = localStorage.getItem("partnerId");

    const [isLoading, setIsLoading] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [confirmation, setConfirmation] = useState({ show: false, type: "" });

    const [partnershipData, setPartnershipData] = useState("");
    const [partnerData, setPartnerData] = useState("");

    const [fileSrc, setFileSrc] = useState("");
    const [fileType, setFileType] = useState("");

    const agreement = contractTemplates["Freight"];
    const userRole = localStorage.getItem("userRole");
    const currentStatus = partnershipData?.status;

    const { state } = useLocation();
    const singlePartnerData = state?.singlePartnerData;

    const objectUrlRef = useRef(null);

    const fileName =
        userRole === "partner"
            ? partnerData?.uploadPartnerContractPDF
            : singlePartnerData?.uploadPartnerContractPDF;

    /* ======================
       FETCH FILE (PDF / IMAGE)
    ======================= */
    useEffect(() => {
        if (!fileName) {
            setFileSrc("");
            setFileType("");
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
            return;
        }

        const controller = new AbortController();

        const cleanupUrl = () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };

        (async () => {
            try {
                const res = await getPrivateFileBlob(fileName, controller.signal);
                const blob = res.data;

                if (!blob || !blob.size) throw new Error("Empty file");

                const type =
                    blob.type || res.headers?.["content-type"] || "";

                setFileType(type);

                const url = URL.createObjectURL(blob);
                cleanupUrl();
                objectUrlRef.current = url;
                setFileSrc(url);
            } catch (err) {
                if (!controller.signal.aborted) {
                    console.error(err);
                    cleanupUrl();
                    setFileSrc("");
                    setFileType("");
                }
            }
        })();

        return () => {
            controller.abort();
            cleanupUrl();
        };
    }, [fileName]);

    /* ======================
       FETCH PROFILE
    ======================= */
    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const res = await getAdminProfile();
            setPartnerData(res?.data?.data?.user);
            setPartnershipData(
                res?.data?.data?.user?.partnershipContract || {}
            );
        } catch (err) {
            console.error("Error fetching profile:", err);
        }
    };

    /* ======================
       CONFIRMATION HANDLERS
    ======================= */
    const handleOpenAcceptModal = () =>
        setConfirmation({ show: true, type: "accept" });

    const handleOpenRejectModal = () => {
        setRejectReason("");
        setConfirmation({ show: true, type: "reject" });
    };

    const handleCloseModal = () =>
        setConfirmation({ show: false, type: "" });

    const handleConfirmAction = async () => {
        try {
            setIsLoading(true);

            const isChecked =
                document.getElementById("agreementCheck")?.checked;

            if (
                (confirmation.type === "accept" ||
                    confirmation.type === "reject") &&
                !isChecked
            ) {
                toast.error(
                    "Please agree to the terms before confirming."
                );
                return;
            }

            const status =
                confirmation.type === "accept"
                    ? "accepted"
                    : "rejected";

            const res = await updatePartnershipContractStatus(
                partnerId,
                status,
                rejectReason,
                type
            );

            toast.success(
                res?.data?.message || "Action completed successfully."
            );

            fetchProfile();
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                "Something went wrong."
            );
        } finally {
            setIsLoading(false);
            handleCloseModal();
        }
    };

    /* ======================
       RENDER
    ======================= */
    return (
        <div className="container-fluid">
            <div className="row dashboardSection">
                {!fileName ? (
                    <div className="p-4 border rounded bg-white shadow-sm contractCardFull col-md-12 text-center fw-bold">
                        No contract available
                    </div>
                ) : (
                    <>
                        <div>
                            {/* PDF VIEWER */}
                            {fileType === "application/pdf" && (
                                <iframe
                                    src={fileSrc}
                                    title="Vendor Contract"
                                    width="100%"
                                    height="700"
                                    style={{ border: "none" }}
                                />
                            )}

                            {/* IMAGE VIEWER */}
                            {fileType.startsWith("image/") && (
                                <img
                                    src={fileSrc}
                                    alt="Vendor Contract"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "700px",
                                        display: "block",
                                        margin: "0 auto",
                                    }}
                                />

                            )}

                            {/* FALLBACK */}
                            {!fileType.startsWith("image/") &&
                                fileType !==
                                "application/pdf" && (
                                    <div className="text-center fw-bold mt-4">
                                        Unsupported file format
                                    </div>
                                )}
                        </div>

                        {currentStatus === "pending" &&
                            userRole === "partner" && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="agreementCheck"
                                            />
                                            <label
                                                htmlFor="agreementCheck"
                                                className="form-check-label"
                                            >
                                                I agree to all the terms and
                                                conditions.
                                            </label>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-3 mt-3">
                                        <button
                                            className="btn btn-outline-primary px-4"
                                            onClick={
                                                handleOpenRejectModal
                                            }
                                            disabled={isLoading}
                                        >
                                            Reject
                                        </button>
                                        <button
                                            className="btn btn-outline-accept px-4"
                                            onClick={
                                                handleOpenAcceptModal
                                            }
                                            disabled={isLoading}
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </>
                            )}
                    </>
                )}

                {/* CONFIRMATION MODAL */}
                <ConfirmationModal
                    show={confirmation.show}
                    title={
                        confirmation.type === "accept"
                            ? "Confirm acceptance"
                            : "Confirm rejection"
                    }
                    onConfirm={handleConfirmAction}
                    onCancel={handleCloseModal}
                    confirmText={
                        confirmation.type === "accept"
                            ? "Yes, accept"
                            : "Yes, reject"
                    }
                    cancelText="Cancel"
                >
                    {confirmation.type === "reject" ? (
                        <>
                            <p>
                                Please specify the reason for rejecting
                                this agreement.
                            </p>
                            <textarea
                                value={rejectReason}
                                onChange={(e) =>
                                    setRejectReason(e.target.value)
                                }
                                placeholder="Enter reason for rejection..."
                                rows="4"
                                style={{
                                    width: "100%",
                                    marginTop: "10px",
                                    padding: "8px",
                                    borderRadius: "5px",
                                    border: "1px solid #ccc",
                                }}
                            />
                        </>
                    ) : (
                        <p>
                            Are you sure you want to accept this
                            vendor agreement?
                        </p>
                    )}
                </ConfirmationModal>
            </div>
        </div>
    );
}

